import { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { UtilityLog } from "@/components/UtilityLog";
import { SmsConfirmDialog } from "@/components/SmsConfirmDialog";
import { TenantFields, todayYMD } from "@/components/TenantFields";
import { EventTimeline, NoteBox, LockoutButtons } from "@/components/TenantTimeline";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, Plus, Check, Trash2, Gauge, User, Wrench, Lock, Bell, MessageSquareText, History, ExternalLink, Reply } from "lucide-react";

const TABS = [
  { key: "details", label: "Details", icon: User },
  { key: "maintenance", label: "Maintenance", icon: Wrench },
  { key: "utility", label: "Utility Log", icon: Gauge },
  { key: "messages", label: "Messages", icon: History },
  { key: "history", label: "History", icon: History },
];

const METERED_UNITS = ["RAZ-J1", "RAZ-J2", "RAZ-J3", "RAZ-J4"];
const isMetered = (u) => !!u && METERED_UNITS.includes(u.unit_number);

export const UnitDrawer = ({ unit, open, initialTab = "details", onClose, onSaved, onDeleted }) => {
  const { user } = useAuth();
  const [tab, setTab] = useState("details");
  const [form, setForm] = useState(null);
  const [readings, setReadings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [events, setEvents] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [saving, setSaving] = useState(false);

  // messaging modals
  const [payModal, setPayModal] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const [reviewModal, setReviewModal] = useState(null); // { preview, pendingStatus }
  const [reviewBusy, setReviewBusy] = useState(false);

  const metered = isMetered(unit);
  const tabs = metered ? TABS : TABS.filter((t) => t.key !== "utility");

  useEffect(() => {
    if (unit) {
      let fn = unit.first_name || "";
      let sn = unit.surname || "";
      if (!fn && !sn && unit.tenant_name) {
        const parts = unit.tenant_name.trim().split(/\s+/);
        fn = parts[0] || "";
        sn = parts.slice(1).join(" ");
      }
      setForm({
        first_name: fn,
        surname: sn,
        address: unit.address || "",
        phone: unit.phone || "",
        email: unit.email || "",
        size: unit.size || "",
        status: unit.status || "vacant",
        notes: unit.notes || "",
        gate_fob: unit.gate_fob || "",
        reserved_until: unit.reserved_until || "",
        move_in_date: unit.move_in_date || "",
        notice_given_date: unit.notice_given_date || "",
        move_out_date: unit.move_out_date || "",
        tasks: unit.tasks || [],
      });
    }
  }, [unit]);

  useEffect(() => {
    if (unit) {
      const wantUtility = initialTab === "utility" && isMetered(unit);
      setTab(wantUtility ? "utility" : "details");
      if (isMetered(unit)) {
        api.getReadings(unit.id).then(setReadings).catch(() => setReadings([]));
      } else {
        setReadings([]);
      }
      api.getUnitMessages(unit.id).then(setMessages).catch(() => setMessages([]));
      api.getUnitEvents(unit.id).then(setEvents).catch(() => setEvents([]));
    }
  }, [unit, initialTab]);

  if (!unit || !form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reloadMessages = () => api.getUnitMessages(unit.id).then(setMessages).catch(() => {});
  const reloadEvents = () => api.getUnitEvents(unit.id).then(setEvents).catch(() => {});
  const afterGate = async () => {
    reloadEvents();
    const list = await api.getUnits({ search: unit.unit_number }).catch(() => []);
    const fresh = list.find((u) => u.id === unit.id);
    onSaved(fresh || null, true);
  };

  const applyStatus = (v) => {
    const clearing = v === "vacant" || v === "needs_cleaning";
    setForm((f) => ({
      ...f,
      status: v,
      ...(clearing && unit.status === "occupied" && !f.move_out_date ? { move_out_date: todayYMD() } : {}),
      ...((v === "occupied" || v === "reserved") && !f.move_in_date ? { move_in_date: todayYMD() } : {}),
      ...(!clearing && unit.status === "occupied" ? { move_out_date: unit.move_out_date || "" } : {}),
    }));
  };

  const onStatusChange = async (v) => {
    const clearing = v === "vacant" || v === "needs_cleaning";
    // Departing tenant with a mobile → offer a Google review text before clearing their details
    if (clearing && unit.status === "occupied" && (unit.phone || "").trim()) {
      try {
        const preview = await api.reviewRequest(unit.id, false);
        setReviewModal({ preview, pendingStatus: v });
        return;
      } catch (e) {
        console.error("Review preview failed; falling back to standard clearing confirm:", e);
      }
    }
    if (clearing && unit.status === "occupied") {
      const label = v === "vacant" ? "Vacant" : "Needs Cleaning";
      const ok = window.confirm(`Change ${unit.unit_number} to "${label}"? This permanently clears the tenant's name, contact details and notes from this unit.`);
      if (!ok) return;
    }
    applyStatus(v);
  };

  const confirmReviewSend = async () => {
    setReviewBusy(true);
    try {
      const r = await api.reviewRequest(unit.id, true);
      if (r.sent) toast.success("Review request text sent");
      else toast.error(r.send_error || "Review text not sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send review text");
    } finally {
      setReviewBusy(false);
    }
    reloadMessages();
    applyStatus(reviewModal.pendingStatus);
    setReviewModal(null);
  };

  const skipReviewSend = () => {
    const v = reviewModal.pendingStatus;
    setReviewModal(null);
    applyStatus(v);
  };

  const openPayment = async () => {
    const lastContact = events.find((e) => e.type === "contact");
    if (lastContact && Date.now() - new Date(lastContact.created_at).getTime() < 3 * 86400000
      && !window.confirm(`Contacted ${new Date(lastContact.created_at).toLocaleString("en-NZ")} by ${lastContact.by} · ${lastContact.method}.\n\nSend another reminder anyway?`)) return;
    try {
      const preview = await api.paymentReminder(unit.id, false);
      setPayModal(preview);
    } catch {
      toast.error("Could not prepare payment reminder");
    }
  };

  const sendPayment = async () => {
    setPayBusy(true);
    try {
      const r = await api.paymentReminder(unit.id, true);
      if (r.sent) toast.success("Payment reminder text sent");
      else toast.error(r.send_error || "Reminder not sent");
      setPayModal(null);
      reloadMessages();
      reloadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reminder");
    } finally {
      setPayBusy(false);
    }
  };

  const removeUnit = async () => {
    if (!window.confirm(`Delete unit ${unit.unit_number}? This also removes its meter readings.`)) return;
    try {
      await api.adminDeleteUnit(unit.id);
      toast.success(`${unit.unit_number} deleted`);
      onDeleted?.(unit.id);
    } catch {
      toast.error("Failed to delete unit");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.updateUnit(unit.id, form);
      toast.success(`${unit.unit_number} updated`);
      onSaved(updated);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    set("tasks", [
      ...form.tasks,
      { id: crypto.randomUUID(), title: newTask.trim(), done: false, priority: "normal", created_at: new Date().toISOString() },
    ]);
    setNewTask("");
  };

  const toggleTask = (id) =>
    set("tasks", form.tasks.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));

  const removeTask = (id) => set("tasks", form.tasks.filter((t) => t.id !== id));

  const addReading = async (body) => {
    const r = await api.addReading(unit.id, body);
    setReadings((prev) => [r, ...prev]);
    onSaved(null, true);
  };

  const delReading = async (rid) => {
    await api.deleteReading(unit.id, rid);
    api.getReadings(unit.id).then(setReadings).catch(() => setReadings((prev) => prev.filter((r) => r.id !== rid)));
    onSaved(null, true);
  };

  const statusLabel = reviewModal?.pendingStatus === "needs_cleaning" ? "Needs Cleaning" : "Vacant";

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl p-0 flex flex-col gap-0" data-testid="unit-drawer">
        <SheetHeader className="px-6 py-4 border-b border-slate-200 space-y-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-mono-nums text-2xl font-bold text-slate-900">
              {unit.unit_number}
            </SheetTitle>
            <StatusBadge status={form.status} />
          </div>
          <SheetDescription className="text-sm text-slate-500">{unit.site_name} · {form.size || "No size"}</SheetDescription>
        </SheetHeader>

        <div className="flex border-b border-slate-200 px-6 overflow-x-auto no-scrollbar whitespace-nowrap">
  {tabs.map((t) => {
    const Icon = t.icon;
    return (
      <button
        key={t.key}
        data-testid={`drawer-tab-${t.key}`}
        onClick={() => setTab(t.key)}
        className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 -mb-px transition-colors shrink-0 ${
          tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
        }`}
      >
        <Icon className="h-4 w-4" /> {t.label}
      </button>
    );
  })}
</div>

        <div className="flex-1 overflow-auto px-6 py-5 space-y-4">
          {tab === "details" && (
            <>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={onStatusChange}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vacant">Vacant</SelectItem>
                    <SelectItem value="occupied">Occupied</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="needs_cleaning">Needs Cleaning</SelectItem>
                    <SelectItem value="management">Management / Internal Use</SelectItem>
                    <SelectItem value="reserved">Reserved</SelectItem>
                  </SelectContent>
                </Select>
                {form.status === "management" && (
                  <p className="text-xs text-slate-500 flex items-start gap-1 mt-1" data-testid="management-note">
                    <Lock className="h-3 w-3 mt-0.5 shrink-0" />
                    Internal use / facility overhead — excluded from availability, occupancy and tenant invoicing. Use the name field for the assigned internal staff member.
                  </p>
                )}
              </div>

              {unit.status === "occupied" && (form.status === "vacant" || form.status === "needs_cleaning") && (
                <p className="text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2" data-testid="move-out-pending-note">
                  Check the move-out date below, then press Save. The tenant's details move to History and are cleared from this shed.
                </p>
              )}

              <TenantFields
                form={form}
                set={set}
                prefix="drawer"
                sizeSlot={(
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1">Size <Lock className="h-3 w-3 text-slate-400" /></Label>
                    <Input data-testid="size-input" value={form.size} readOnly disabled className="bg-slate-100 text-slate-500 cursor-not-allowed" />
                  </div>
                )}
              />

              {form.status === "reserved" && (
                <div className="space-y-1.5">
                  <Label>Reserved Until (move-in date)</Label>
                  <Input data-testid="reserved-until-input" type="date" value={form.reserved_until} onChange={(e) => set("reserved_until", e.target.value)} />
                </div>
              )}

              {(unit.status === "occupied" || unit.gate_locked) && (
                <div className={`rounded-lg border p-3 flex flex-wrap items-center justify-between gap-2 ${unit.gate_locked ? "border-red-300 bg-red-50" : "border-slate-200"}`} data-testid="drawer-gate-access">
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                    <Lock className="h-4 w-4 text-slate-500" /> {unit.gate_locked ? <span className="text-red-700" data-testid="drawer-locked-badge">Locked out{unit.gate_locked_reason ? ` — ${unit.gate_locked_reason}` : ""}</span> : "Gate access"}
                  </p>
                  <LockoutButtons unit={unit} onDone={afterGate} />
                </div>
              )}

              {unit.status === "occupied" && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="tenant-messaging">
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                    <MessageSquareText className="h-4 w-4 text-slate-500" /> Tenant Messaging
                  </p>
                  <Button
                    data-testid="send-payment-reminder-btn"
                    variant="outline"
                    onClick={openPayment}
                    className="w-full gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <Bell className="h-4 w-4" /> Send Payment Reminder (SMS)
                  </Button>
                  <p className="text-[11px] text-slate-400">A Google review request text is offered automatically when you mark this unit Vacant or Needs Cleaning.</p>
                </div>
              )}
            </>
          )}

          {tab === "maintenance" && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  data-testid="task-input"
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addTask()}
                  placeholder="New maintenance task..."
                />
                <Button data-testid="task-add" onClick={addTask} className="bg-slate-900 hover:bg-slate-700">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {form.tasks.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No maintenance tasks.</p>
              ) : (
                <ul className="space-y-2">
                  {form.tasks.map((t) => (
                    <li key={t.id} data-testid={`task-${t.id}`} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2">
                      <button onClick={() => toggleTask(t.id)} className={`h-5 w-5 rounded border flex items-center justify-center ${t.done ? "bg-green-500 border-green-500 text-white" : "border-slate-300"}`}>
                        {t.done && <Check className="h-3.5 w-3.5" />}
                      </button>
                      <span className={`flex-1 text-sm ${t.done ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</span>
                      <button onClick={() => removeTask(t.id)} className="text-slate-300 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-slate-400">Tip: set status to "Maintenance" and save to flag this unit.</p>
            </div>
          )}

          {tab === "utility" && (
            <UtilityLog unitId={unit?.id} readings={readings} onAdd={addReading} onDelete={delReading} onInvoiced={() => { api.getReadings(unit.id).then(setReadings).catch(() => {}); reloadEvents(); }} />
          )}

          {tab === "history" && (
            <div className="space-y-3">
              <div className="space-y-2 pb-3 border-b border-slate-200" data-testid="drawer-contact-timeline">
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Contacts, notes &amp; gate access</Label>
                <NoteBox unitId={unit.id} onDone={reloadEvents} />
                <EventTimeline events={events} onChanged={reloadEvents} />
              </div>
              <div className="flex justify-between items-center">
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Past Tenancies Log
                </Label>
                <span className="text-xs text-slate-400">
                  {unit?.history?.length || 0} total archived
                </span>
              </div>

              {unit?.history && unit.history.length > 0 ? (
                <div className="space-y-3">
                  {unit.history.slice().reverse().map((record, idx) => (
                    <div
                      key={record.id || idx}
                      className="p-3.5 bg-slate-50 rounded-lg border border-slate-200 space-y-1.5 text-sm"
                      data-testid={`history-record-${idx}`}
                    >
                      <div className="flex justify-between items-start font-semibold text-slate-800">
                        <span>
                          {record.tenant_name || `${record.first_name || ""} ${record.surname || ""}`.trim() || "Unknown Tenant"}
                        </span>
                        {record.gate_fob && (
                          <span className="text-xs font-normal text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-200">
                            Fob: {record.gate_fob}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 pt-1">
                        <div>📅 Moved In: <strong className="text-slate-700">{record.move_in_date || "N/A"}</strong></div>
                        <div>🚪 Moved Out: <strong className="text-slate-700">{record.move_out_date || "N/A"}</strong></div>
                      </div>

                      {(record.phone || record.email) && (
                        <div className="text-xs text-slate-500 pt-1 flex flex-wrap gap-x-3">
                          {record.phone && <span>📞 {record.phone}</span>}
                          {record.email && <span>✉️ {record.email}</span>}
                        </div>
                      )}

                      {record.notes && (
                        <div className="text-xs text-slate-500 italic mt-1 bg-white p-2 rounded border border-slate-100">
                          "{record.notes}"
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-400 text-sm bg-slate-50 rounded-lg border border-dashed border-slate-200">
                  No previous tenancies recorded for this shed yet.
                </div>
              )}
            </div>
          )}

          {tab === "messages" && (
            <div className="space-y-2.5" data-testid="messages-log">
              {messages.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No reminder or review texts have been sent for this unit yet.</p>
              ) : (
                messages.map((m) => {
                  if (m.direction === "in") {
                    const rwhen = m.created_at ? new Date(m.created_at).toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
                    return (
                      <div key={m.id} data-testid={`message-row-${m.id}`} className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 ml-6">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1"><Reply className="h-3 w-3" /> Tenant reply</span>
                          <span className="text-xs text-slate-400 font-mono-nums">{rwhen}</span>
                        </div>
                        <p className="text-xs text-slate-700 whitespace-pre-line mt-1.5">{m.message}</p>
                      </div>
                    );
                  }
                  const isReview = m.type === "review_request";
                  const typeLabel = isReview ? "Review request" : "Payment reminder";
                  const when = m.created_at ? new Date(m.created_at).toLocaleString("en-NZ", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
                  const d = m.delivery_status;
                  const delivery = m.status !== "sent"
                    ? { cls: "bg-red-50 text-red-600 border-red-200", label: "Not sent" }
                    : d === "delivered" ? { cls: "bg-green-50 text-green-700 border-green-200", label: "Delivered" }
                    : d === "failed" ? { cls: "bg-red-50 text-red-600 border-red-200", label: "Not delivered" }
                    : { cls: "bg-slate-50 text-slate-500 border-slate-200", label: "Pending delivery" };
                  return (
                    <div key={m.id} data-testid={`message-row-${m.id}`} className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${isReview ? "bg-violet-50 text-violet-700 border border-violet-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{typeLabel}</span>
                        <span className="text-xs text-slate-400 font-mono-nums">{when}</span>
                      </div>
                      <p className="text-xs text-slate-600 whitespace-pre-line mt-1.5">{m.message}</p>
                      <div className="flex items-center flex-wrap gap-2 mt-2 text-xs">
                        <span className={`rounded-full px-2 py-0.5 font-medium border ${delivery.cls}`} data-testid={`message-status-${m.id}`}>
                          {delivery.label}
                        </span>
                        {m.phone && <span className="text-slate-400 font-mono-nums">{m.phone}</span>}
                        {isReview && (
                          <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${m.click_count > 0 ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-slate-50 text-slate-500 border border-slate-200"}`} data-testid={`message-clicks-${m.id}`}>
                            <ExternalLink className="h-3 w-3" />
                            {m.click_count > 0 ? `Review opened ${m.click_count}×` : "Not opened yet"}
                          </span>
                        )}
                      </div>
                      {m.sent_by && <p className="text-[11px] text-slate-400 mt-1.5">Sent by {m.sent_by}</p>}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-3 sm:px-6 py-3 flex items-center justify-between gap-2 bg-white flex-wrap sm:flex-nowrap">
  {user?.role === "admin" && (
    <Button variant="outline" onClick={removeUnit} data-testid="delete-unit-button" className="border-red-200 text-red-600 hover:bg-red-50 shrink-0">
      <Trash2 className="h-4 w-4 mr-1.5" /> Delete
    </Button>
  )}
  <div className="flex items-center gap-2 ml-auto">
    <Button variant="outline" onClick={onClose} data-testid="drawer-close" className="shrink-0">Close</Button>
    <Button data-testid="save-unit-details-button" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 shrink-0">
      <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving..." : "Save Changes"}
    </Button>
  </div>
</div>
</SheetContent>

<SmsConfirmDialog
  open={!!payModal}
  onOpenChange={(o) => !o && setPayModal(null)}
  preview={payModal}
  busy={payBusy}
  onConfirm={sendPayment}
  confirmLabel="Send Reminder"
  title="Send Payment Reminder"
  description="A payment reminder text will be sent to this tenant."
/>

<SmsConfirmDialog
  open={!!reviewModal}
  onOpenChange={(o) => !o && setReviewModal(null)}
  preview={reviewModal?.preview}
  busy={reviewBusy}
  onConfirm={confirmReviewSend}
  confirmLabel={`Send review & mark ${statusLabel}`}
  title={`Mark ${unit.unit_number} ${statusLabel}`}
  description={`This clears the tenant's details. Send ${reviewModal?.preview?.site_name || ""} a Google review request text first?`}
  footerExtra={
    <Button variant="outline" onClick={skipReviewSend} data-testid="sms-skip" className="border-slate-300">
      {statusLabel} without sending
    </Button>
  }
/>
</Sheet>
  );
};